import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { store } from '../../redux/store';
import { storePaymentStatus } from '../../redux/reducers/paymentGatewaySlice';
import { storeBillingAmount } from '../../redux/reducers/accountSlice';
import Modal from '../modal/Modal';
import { AlertSVG, InfoSVG } from '../svgs';
import Loader from '../loader/Loader';
import { ValidatedState } from '../custom-input/ValidatedInput';
import PaymentDetails from './PaymentDetails';
import BillingAddress from './BillingAddress';
import { centsToDollars, setActiveAccount } from '../../services/account.service';
import { mapPaymentInfo } from '../../services/payment.service';
import { showToast } from '../../services/toast.service';
import {
  addPaymentDetails,
  clearOutstandingBalance,
  makeInstantPayment,
  updatePaymentDetails,
} from '../../api/paymentGatewayApi';
import { payOnFarmOutstandingBalance } from '../../api/onFarmPaymentTransactionApi';
import { AchPaymentDetails, CardPaymentDetails } from '../../types/interfaces';
import { PaymentMethod } from '../../types/interfaces/paymentMethod.interface';
import { PaymentMethods, PaymentActions, PayNowPaymentMethods, OnFarmPaymentStatus } from '../../types/enums';
import { ROUTE_PATHS } from '../../constants/routePaths';
import { toastMessages } from '../../constants/errorMessages';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, NOTIFICATION_CONSTANTS, PAYMENT_CONSTANTS } from '../../constants/common';
import './addPaymentMethodModal.scss';

export const initialPaymentState = {
  accountNumber: { value: '', valid: false },
  accountType: { value: '', name: '' },
  address: { value: '', valid: false },
  cardNumber: { value: '', valid: false },
  city: { value: '', valid: false },
  country: { value: '', valid: false },
  cvv: { value: '', valid: false },
  expiry: { value: '', valid: false },
  name: { value: '', valid: false },
  paymentType: { value: PaymentMethods.CARD, valid: true },
  postalCode: { value: '', valid: false },
  region: { value: '', valid: false },
  routingNumber: { value: '', valid: false },
  isDefault: false,
};

type AddPaymentMethodModalProps = {
  accountId: number;
  description?: string;
  buttonAction: string;
  isOnFarmPayNow?: boolean;
  isOpen: boolean;
  onclick: () => void;
  paymentDetails?: PaymentMethod;
  setIsOpen: (e: boolean) => void;
  setUpdatedPaymentInfo?: (e: boolean) => void;
  title: string;
  lockPaymentTypeSelection?: boolean;
  accountHasCard?: boolean;
  addSuccessNavigatePath?: string;
  addSuccessNavigateState?: unknown;
  onAddPaymentDetails?: (accountId: number, paymentInfo: CardPaymentDetails | AchPaymentDetails) => Promise<void>;
  onUpdatePaymentDetails?: (accountId: number, paymentInfo: CardPaymentDetails | AchPaymentDetails) => Promise<void>;
  hideDefaultPaymentOption?: boolean;
  skipAddSuccessNavigation?: boolean;
  useMarketplaceAchAddressLayout?: boolean;
};

const AddPaymentMethodModal: FC<AddPaymentMethodModalProps> = ({
  accountId,
  description,
  buttonAction,
  isOnFarmPayNow = false,
  isOpen,
  onclick,
  paymentDetails,
  setIsOpen,
  setUpdatedPaymentInfo,
  title,
  lockPaymentTypeSelection = false,
  accountHasCard = false,
  addSuccessNavigatePath,
  addSuccessNavigateState,
  onAddPaymentDetails,
  onUpdatePaymentDetails,
  hideDefaultPaymentOption = false,
  skipAddSuccessNavigation = false,
  useMarketplaceAchAddressLayout = false,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activeAccount = useAppSelector(state => state.activeAccount);

  const [addingPaymentMethod, setAddingPaymentMethod] = useState<boolean>(true);
  const [clearingBalance, setClearingBalance] = useState<boolean>(true);
  const [confirmAccountNumber, setConfirmAccountNumber] = useState<ValidatedState>({ value: '', valid: false });
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(initialPaymentState);
  const [payNowPaymentMethod, setPayNowPaymentMethod] = useState<string>('');
  const [validate, setValidate] = useState<boolean>(false);

  const isAddPaymentFlow = buttonAction === PaymentActions.ADD_PAYMENT_METHOD;
  const restrictToAchInAddFlow = isAddPaymentFlow && accountHasCard;

  useEffect(() => {
    if (buttonAction === PaymentActions.PAY_NOW_METHOD) {
      if (paymentDetails != undefined) {
        setPaymentMethod({
          ...paymentDetails,
        });
      }
      if (paymentDetails?.paymentType?.value === PaymentMethods.ACH) {
        setPayNowPaymentMethod(PayNowPaymentMethods.NEW);
        setPaymentMethod(initialPaymentState);
      } else {
        setPayNowPaymentMethod(PayNowPaymentMethods.EXISTING);
      }
      return;
    }

    if (paymentDetails != undefined) {
      setPaymentMethod({
        ...paymentDetails,
      });
      if (buttonAction === PaymentActions.UPDATE_PAYMENT_METHOD && paymentDetails.paymentType?.value === PaymentMethods.ACH) {
        setConfirmAccountNumber({
          value: paymentDetails.accountNumber?.value ?? '',
          valid: Boolean(paymentDetails.accountNumber?.value && paymentDetails.accountNumber?.valid),
        });
      } else {
        setConfirmAccountNumber({ value: '', valid: false });
      }
    }
  }, [paymentDetails, buttonAction]);

  const checkValidity = () => {
    const { address, city, region, postalCode, country, cardNumber, expiry, cvv, accountType, accountNumber, routingNumber } =
      paymentMethod || {};

    const isCardPayment = paymentMethod?.paymentType?.value === PaymentMethods.CARD;
    const isExistingPayNow =
      buttonAction === PaymentActions.PAY_NOW_METHOD && payNowPaymentMethod === PayNowPaymentMethods.EXISTING;

    const isBillingAddressValid = address?.valid && city?.valid && region?.valid && postalCode?.valid && country?.valid;
    const isCardValid = cardNumber?.valid && expiry?.valid && (isExistingPayNow || cvv?.valid);
    const usesMaskedAch =
      !isCardPayment &&
      paymentMethod?.usesExistingMaskedAccount === true &&
      buttonAction === PaymentActions.UPDATE_PAYMENT_METHOD;
    const isAccountValid = usesMaskedAch
      ? accountType?.value?.length > 0 && routingNumber?.valid && accountNumber?.value === confirmAccountNumber?.value
      : accountType?.value?.length > 0 &&
        accountNumber?.valid &&
        routingNumber?.valid &&
        accountNumber?.value === confirmAccountNumber?.value;
    return (
      paymentMethod?.paymentType?.valid &&
      paymentMethod?.name?.valid &&
      isBillingAddressValid &&
      (isCardPayment ? isCardValid : isAccountValid)
    );
  };

  const checkOutstandingBalance = () => {
    if (isOnFarmPayNow) {
      return activeAccount?.onFarmOutstandingBalanceCents > 0;
    }
    return activeAccount?.billingAmount?.outstandingBalanceCents > 0 || activeAccount?.billingAmount?.penaltyCents > 0;
  };

  const getOutstandingBalanceDisplayAmount = () => {
    if (isOnFarmPayNow) {
      return activeAccount?.onFarmOutstandingBalanceCents ?? 0;
    }
    return activeAccount?.billingAmount?.totalFineAmountCents + (activeAccount?.billingAmount?.quickbooksLateFeeCents ?? 0);
  };

  const isOnFarmPaymentSuccessful = (status?: string) => {
    return status === OnFarmPaymentStatus.SUCCESSFUL || status === OnFarmPaymentStatus.PENDING;
  };

  const completePayNowSuccess = async () => {
    showToast.success(toastMessages.PAYMENT_SUCCESS);
    await setActiveAccount(accountId);
    if (!isOnFarmPayNow) {
      store.dispatch(storeBillingAmount({ ...activeAccount.billingAmount, outstandingBalanceCents: 0 }));
      navigate(ROUTE_PATHS.APP_DASHBOARD);
    }
    setIsOpen(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!addingPaymentMethod) return;
    setAddingPaymentMethod(false);
    setValidate(true);

    const paymentInfo = mapPaymentInfo(paymentMethod, {
      isUpdate: buttonAction === PaymentActions.UPDATE_PAYMENT_METHOD,
    }) as CardPaymentDetails | AchPaymentDetails;

    if (accountId) {
      try {
        if (buttonAction === PaymentActions.ADD_PAYMENT_METHOD) {
          if (onAddPaymentDetails) {
            await onAddPaymentDetails(accountId, paymentInfo);
          } else {
            await addPaymentDetails(accountId, paymentInfo);
          }
          showToast.success(toastMessages.ADD_PAYMENT_METHOD_SUCCESS);
          setActiveAccount(accountId);
          setUpdatedPaymentInfo && setUpdatedPaymentInfo(true);
          if (!skipAddSuccessNavigation) {
            navigate(addSuccessNavigatePath ?? ROUTE_PATHS.APP_DASHBOARD, { state: addSuccessNavigateState });
          }
          setIsOpen(false);
        } else if (buttonAction === PaymentActions.UPDATE_PAYMENT_METHOD) {
          if (onUpdatePaymentDetails) {
            await onUpdatePaymentDetails(accountId, paymentInfo);
          } else {
            await updatePaymentDetails(accountId, paymentInfo);
          }
          setUpdatedPaymentInfo && setUpdatedPaymentInfo(true);
          showToast.success(toastMessages.ADD_PAYMENT_UPDATED);
          setActiveAccount(accountId);
          setIsOpen(false);
        } else if (buttonAction === PaymentActions.PAY_NOW_METHOD) {
          if (payNowPaymentMethod === PayNowPaymentMethods.EXISTING) {
            await handleRetryPayment();
          } else if (payNowPaymentMethod === PayNowPaymentMethods.NEW) {
            await handleInstantPayment(paymentInfo);
          }
        }

        dispatch(storePaymentStatus(true));
      } catch (error: any) {
        if (error?.response?.status === 701) {
          showToast.error(error.response?.data?.title);
        } else if (error?.response?.status === 409) {
          showToast.error(toastMessages.PAYMENT_METHOD_PURPOSE_CONFLICT);
        } else {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        }
        setIsOpen(true);
        setValidate(false);
      } finally {
        setAddingPaymentMethod(true);
      }
    }
  };

  const handleRetryPayment = async () => {
    if (!clearingBalance) return;
    setClearingBalance(false);

    try {
      if (isOnFarmPayNow) {
        const { data: paymentResponse } = await payOnFarmOutstandingBalance(accountId, {});
        if (isOnFarmPaymentSuccessful(paymentResponse?.status)) {
          await completePayNowSuccess();
        } else {
          showToast.error(toastMessages.PAYMENT_FAIL);
        }
      } else {
        const response = await clearOutstandingBalance(accountId);

        if (response?.data?.status === 'A') {
          await completePayNowSuccess();
        } else {
          showToast.error(toastMessages.PAYMENT_FAIL);
        }
      }
    } catch (error: any) {
      if (error?.response?.data?.title) {
        showToast.error(error.response.data.title);
      } else {
        showToast.error(toastMessages.PAYMENT_FAIL);
      }
      setIsOpen(true);
      setValidate(false);
    } finally {
      setClearingBalance(true);
    }
  };

  const handleInstantPayment = async (paymentInfo?: any) => {
    if (!clearingBalance) return;
    setClearingBalance(false);

    try {
      if (isOnFarmPayNow) {
        const { data: paymentResponse } = await payOnFarmOutstandingBalance(accountId, {});
        if (isOnFarmPaymentSuccessful(paymentResponse?.status)) {
          await completePayNowSuccess();
        } else {
          showToast.error(toastMessages.PAYMENT_FAIL);
        }
      } else {
        const { data: paymentResponse } = await makeInstantPayment(accountId, paymentInfo);

        if (paymentResponse?.status === 'A') {
          await completePayNowSuccess();
        } else {
          showToast.error(toastMessages.PAYMENT_FAIL);
        }
      }
    } catch (error: any) {
      if (error?.response?.status === 409) {
        showToast.error(toastMessages.PAYMENT_METHOD_PURPOSE_CONFLICT);
      } else if (error?.response?.data?.title) {
        showToast.error(error.response.data.title);
      } else {
        showToast.error(toastMessages.PAYMENT_FAIL);
      }
      setIsOpen(true);
      setValidate(false);
    } finally {
      setClearingBalance(true);
    }
  };

  const isThePayNowExistingMethodACH = () => {
    return (
      buttonAction === PaymentActions.PAY_NOW_METHOD &&
      payNowPaymentMethod === PayNowPaymentMethods.NEW &&
      paymentDetails?.paymentType?.value === PaymentMethods.ACH
    );
  };

  useEffect(() => {
    if (buttonAction === PaymentActions.PAY_NOW_METHOD) {
      if (payNowPaymentMethod === PayNowPaymentMethods.EXISTING && paymentDetails) {
        setPaymentMethod({
          ...paymentDetails,
        });
      } else if (payNowPaymentMethod === PayNowPaymentMethods.NEW) {
        setPaymentMethod(initialPaymentState);
      }
    }
  }, [payNowPaymentMethod, buttonAction, paymentDetails]);

  return (
    <Modal ignoreBackdrop isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <div className="add-payment-method">
        <div className="header">
          <h4>{title}</h4>
          {description && (
            <div className="info-banner">
              <InfoSVG size="18" color="#0f64dcf5" />
              {description}
            </div>
          )}
        </div>
        <form action="submit" onSubmit={(event: React.FormEvent) => handleSubmit(event)}>
          <div className="payment-method">
            {!isThePayNowExistingMethodACH() && <label>{PAYMENT_CONSTANTS.PAYMENT_METHOD}</label>}
            {buttonAction === PaymentActions.PAY_NOW_METHOD ? (
              <>
                <div className="radio-row">
                  {paymentDetails?.paymentType?.value === PaymentMethods.CARD && (
                    <>
                      <input
                        type="radio"
                        id="payment-method-card"
                        name="payment-method-card"
                        value={payNowPaymentMethod}
                        onChange={() => setPayNowPaymentMethod(PayNowPaymentMethods.EXISTING)}
                        checked={payNowPaymentMethod === PayNowPaymentMethods.EXISTING}
                      />
                      <label htmlFor="payment-method-card">{PAYMENT_CONSTANTS.EXISTING_METHOD}</label>
                    </>
                  )}
                  {isThePayNowExistingMethodACH() ? (
                    <div className="card-details-note">
                      <p>
                        Please enter card details to resolve your outstanding balance of{' '}
                        <b>${centsToDollars(getOutstandingBalanceDisplayAmount())}</b>.
                      </p>
                    </div>
                  ) : (
                    <>
                      <input
                        type="radio"
                        id="payment-method-ach"
                        name="payment-method-ach"
                        value={payNowPaymentMethod}
                        onChange={() => setPayNowPaymentMethod(PayNowPaymentMethods.NEW)}
                        checked={payNowPaymentMethod === PayNowPaymentMethods.NEW}
                      />
                      <label htmlFor="payment-method-ach">{PAYMENT_CONSTANTS.NEW_METHOD}</label>
                    </>
                  )}
                </div>
              </>
            ) : lockPaymentTypeSelection || restrictToAchInAddFlow ? (
              <div className="payment-method-locked-type">
                <p className="payment-method-locked-type__value">
                  {paymentMethod?.paymentType?.value === PaymentMethods.CARD
                    ? PAYMENT_CONSTANTS.CARD
                    : PAYMENT_CONSTANTS.ACH_TRANSFER}
                </p>
              </div>
            ) : (
              <>
                <div className="radio-row">
                  <input
                    type="radio"
                    id="payment-method-card"
                    name="payment-method-card"
                    value={paymentMethod?.paymentType?.value}
                    onChange={() => {
                      setPaymentMethod({
                        ...paymentMethod,
                        paymentType: { value: PaymentMethods.CARD, valid: true },
                        profileId: undefined,
                        isDefault: false,
                        usesExistingMaskedAccount: false,
                      });
                    }}
                    checked={paymentMethod?.paymentType?.value === PaymentMethods.CARD}
                  />
                  <label htmlFor="payment-method-card">{PAYMENT_CONSTANTS.CARD}</label>
                  <input
                    type="radio"
                    id="payment-method-ach"
                    name="payment-method-ach"
                    value={paymentMethod?.paymentType?.value}
                    onChange={() => {
                      setPaymentMethod({
                        ...paymentMethod,
                        paymentType: { value: PaymentMethods.ACH, valid: true },
                        profileId: undefined,
                        isDefault: false,
                        usesExistingMaskedAccount: false,
                      });
                    }}
                    checked={paymentMethod?.paymentType?.value === PaymentMethods.ACH}
                  />
                  <label htmlFor="payment-method-ach">{PAYMENT_CONSTANTS.ACH_TRANSFER}</label>
                </div>
              </>
            )}
          </div>

          <PaymentDetails
            paymentMethod={paymentMethod}
            validate={validate}
            buttonAction={buttonAction}
            paymentMethodType={payNowPaymentMethod}
            setPaymentMethod={setPaymentMethod}
            confirmAccountNumber={confirmAccountNumber}
            setConfirmAccountNumber={setConfirmAccountNumber}
          />

          {(!clearingBalance || !addingPaymentMethod) && <Loader loaderSize={'medium'} />}

          <BillingAddress
            paymentMethod={paymentMethod}
            validate={validate}
            buttonAction={buttonAction}
            paymentMethodType={payNowPaymentMethod}
            setPaymentMethod={setPaymentMethod}
            useMarketplaceAchAddressLayout={useMarketplaceAchAddressLayout}
          />

          {buttonAction !== PaymentActions.PAY_NOW_METHOD && !hideDefaultPaymentOption && (
            <div className="set-as-default-condition">
              <input
                type="checkbox"
                id="default-pay-method"
                name="default-pay-method"
                value="default-pay-method"
                checked={paymentMethod?.isDefault ?? false}
                onChange={event => setPaymentMethod({ ...paymentMethod, isDefault: event.target.checked })}
              />
              <p>{PAYMENT_CONSTANTS.SET_DEFAULT_PAYMENT_METHOD}</p>
            </div>
          )}

          <div className="payment-transaction-note">
            {paymentMethod?.paymentType?.value === PaymentMethods.CARD && (
              <div className="transaction-alert">
                <AlertSVG />
                <p>{NOTIFICATION_CONSTANTS.CC_PROCESSING_FEE_MSG}</p>
              </div>
            )}
            {paymentMethod?.paymentType?.value === PaymentMethods.ACH && (
              <div className="outstanding-balance-note">
                <p>
                  <b>{LABEL_CONSTANTS.NOTE}:</b> {NOTIFICATION_CONSTANTS.ACH_PAYMENT_METHOD_NOTE}
                </p>
              </div>
            )}
            {checkOutstandingBalance() && buttonAction === PaymentActions.PAY_NOW_METHOD && (
              <div className="outstanding-balance-note">
                {payNowPaymentMethod === PayNowPaymentMethods.EXISTING ? (
                  <p>{NOTIFICATION_CONSTANTS.PAY_NOW_EXISTING_FOOTER}</p>
                ) : (
                  <p>{NOTIFICATION_CONSTANTS.PAY_NOW_NEW_FOOTER}</p>
                )}
              </div>
            )}
          </div>
          <div className="footer">
            <button
              type="submit"
              disabled={!checkValidity() || !clearingBalance || !addingPaymentMethod}
              className="button green medium">
              {buttonAction === PaymentActions.PAY_NOW_METHOD
                ? payNowPaymentMethod === PayNowPaymentMethods.NEW
                  ? BUTTON_CONSTANTS.SAVE_PAY
                  : BUTTON_CONSTANTS.PAY
                : BUTTON_CONSTANTS.SAVE}
            </button>
            <button className="button green inverted mr-1" onClick={onclick} disabled={!clearingBalance || !addingPaymentMethod}>
              {BUTTON_CONSTANTS.CANCEL}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddPaymentMethodModal;
